// The guided tour (lib/tour.ts): a dimmed screen with a cut-out around the step's element, and a card beside it.
// The page behind is inert while it runs (no clicks, no focus); Escape closes it, the arrows and Enter move.

import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useI18n } from '../i18n/index.ts';
import { moveStep, placeCard, presentSteps, TOUR_STEPS, type TourStep } from '../lib/tour.ts';
import { Button } from './ui.tsx';

const PAD = 6;

const find = (target: string | undefined): HTMLElement | null => {
  if (!target) return null;
  const el = document.querySelector<HTMLElement>(`[data-tour="${target}"]`);
  return el && el.getClientRects().length > 0 ? el : null;
};

const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

export function Tour({ admin, onClose }: { admin: boolean; onClose: () => void }) {
  const { t } = useI18n();
  const [id, setId] = useState<TourStep['id']>('welcome');
  const [box, setBox] = useState<DOMRect | null>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const card = useRef<HTMLDivElement>(null);

  // Worked out at each render: the overview's tiles may arrive while the tour is open.
  const steps = presentSteps(TOUR_STEPS, (target) => find(target) !== null);
  const index = Math.max(0, steps.findIndex((s) => s.id === id));
  const step = steps[index]!;
  const last = index === steps.length - 1;
  const go = (by: 1 | -1) => setId(steps[moveStep(index, by, steps.length)]!.id);

  // The page behind: inert while the tour runs, and focus back where it was afterwards.
  useEffect(() => {
    const root = document.getElementById('root');
    const before = document.activeElement as HTMLElement | null;
    if (root) root.inert = true;
    return () => {
      if (root) root.inert = false;
      before?.focus();
    };
  }, []);

  useLayoutEffect(() => {
    const el = find(step.target);
    el?.scrollIntoView({ block: 'center', inline: 'nearest', behavior: reducedMotion() ? 'auto' : 'smooth' });
    let frame = 0;
    const measure = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const r = find(step.target)?.getBoundingClientRect() ?? null;
        const c = card.current?.getBoundingClientRect();
        setBox(r);
        if (c) {
          setPos(placeCard(r && { top: r.top - PAD, left: r.left - PAD, width: r.width + 2 * PAD, height: r.height + 2 * PAD },
            { width: c.width, height: c.height }, { width: window.innerWidth, height: window.innerHeight }));
        }
      });
    };
    measure();
    card.current?.focus();
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
    };
  }, [step]);

  // On the window, so that the keys work wherever the focus went (a click on the dimmed page drops it).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const onButton = e.target instanceof HTMLButtonElement;
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowRight' || (e.key === 'Enter' && !onButton)) { e.preventDefault(); if (last) onClose(); else go(1); }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); go(-1); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Tab') {
      // Focus stays in the card (the page behind is inert, but the browser's own controls are not).
      const items = [...card.current!.querySelectorAll<HTMLElement>('button:not([disabled])')];
      const at = items.indexOf(document.activeElement as HTMLElement);
      const next = e.shiftKey ? (at <= 0 ? items.length - 1 : at - 1) : (at + 1) % items.length;
      e.preventDefault();
      items[next]?.focus();
    }
  };

  const motion = 'transition-all duration-200 motion-reduce:transition-none';
  return createPortal(
    <div className="fixed inset-0 z-50">
      {box ? (
        <div aria-hidden="true" className={`pointer-events-none fixed rounded-lg ${motion}`}
          style={{ top: box.top - PAD, left: box.left - PAD, width: box.width + 2 * PAD, height: box.height + 2 * PAD,
            boxShadow: '0 0 0 9999px rgb(11 34 51 / 0.6)' }} />
      ) : <div aria-hidden="true" className="fixed inset-0 bg-nuit/60" />}
      <div ref={card} role="dialog" aria-modal="true" aria-label={t('tour.label')} aria-describedby="tour-body" tabIndex={-1}
        onKeyDown={onKeyDown}
        className={`fixed w-[min(20rem,calc(100vw-2rem))] rounded-lg bg-white p-4 text-slate-900 shadow-xl outline-none ${pos ? '' : 'opacity-0'}`}
        style={pos ?? { top: 0, left: 0 }}>
        <div className="flex items-start justify-between gap-3">
          <h2 className="text-base font-semibold">{t(`tour.steps.${step.id}.title`)}</h2>
          <button type="button" onClick={onClose} aria-label={t('common.close')} title={t('common.close')}
            className="-my-1 -mr-1 rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700">
            <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"
              className="h-5 w-5" aria-hidden="true"><path d="M5 5l10 10M15 5L5 15" /></svg>
          </button>
        </div>
        <p id="tour-body" className="mt-1.5 text-sm text-slate-600">
          {step.id === 'menu' && admin ? t('tour.menuAdmin') : t(`tour.steps.${step.id}.body`)}
        </p>
        <div className="mt-4 flex items-center justify-between gap-2">
          <span className="text-xs text-slate-500" aria-live="polite">{t('tour.step', { n: index + 1, count: steps.length })}</span>
          <div className="flex gap-2">
            {index > 0 && <Button size="sm" variant="secondary" onClick={() => go(-1)}>{t('tour.previous')}</Button>}
            <Button size="sm" onClick={() => (last ? onClose() : go(1))}>{t(last ? 'tour.finish' : 'tour.next')}</Button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
